/**
 * useTaskExecution — React hook wrapping the full task-execution lifecycle:
 * status polling, WebSocket event handlers, and the action callbacks that
 * trigger workflow transitions on the server.
 */

import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { inflate } from 'pako';
import { useSocket } from '../../contexts/socketTypes';
import { useAuthenticatedApi } from '../useAuthenticatedApi';
import { logger } from '../../utils/logger';
import { useSubStepProgress } from '../useSubStepProgress';
import {
  TaskPhase,
  type TaskStatus,
  type TaskType,
  type StartTaskParams,
  type UseTaskExecutionReturn,
  normalizeQuestions,
} from './types';
import { createTaskSocketHandlers, type TaskSocketHandlers } from './socketHandlers';

export function useTaskExecution(taskId?: string): UseTaskExecutionReturn {
  const api = useAuthenticatedApi();
  const { socket, isConnected, joinTask: joinTaskRoom, leaveTask: leaveTaskRoom } = useSocket();

  // State
  const [taskStatus, setTaskStatus] = useState<TaskStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);
  const [lastAnswers, setLastAnswers] = useState<Record<string, unknown> | undefined>(undefined);

  // Stream live log lines into the running step's description
  useSubStepProgress(socket, setTaskStatus);

  // Download and decompress the diff patch from S3 via a backend-signed URL
  const fetchDiff = useCallback(
    async (taskIdOverride?: string) => {
      const id = taskIdOverride ?? taskId;
      if (!id) return;
      try {
        const { url } = await api.post(`/tasks/${id}/diff/url`, {}).then((r) => r.data);
        const buffer = await fetch(url).then((r) => r.arrayBuffer());
        const text = new TextDecoder().decode(inflate(new Uint8Array(buffer)));
        setTaskStatus((prev) =>
          prev
            ? {
                ...prev,
                preview: {
                  ...(prev.preview ?? { diff: '', files: [], iteration: 0 }),
                  diff: text,
                },
              }
            : null,
        );
      } catch (err) {
        logger.error('Failed to fetch diff from S3:', { error: err });
      }
    },
    [taskId, api],
  );

  // Fetch task status
  const refreshStatus = useCallback(async () => {
    if (!taskId) return;
    logger.info('Fetching task status...', { taskId });
    try {
      setLoading(true);
      const response = await api.get(`/tasks/${taskId}/status`);
      const data = response.data;
      if (data.questions) {
        data.questions = normalizeQuestions(data.questions);
      }
      setTaskStatus(data);
      setError(null);
    } catch (err: unknown) {
      const message = axios.isAxiosError(err) ? err.response?.data?.error : undefined;
      setError(message || 'Failed to fetch task status');
    } finally {
      setLoading(false);
    }
  }, [taskId, api]);

  // Fetch available task types
  useEffect(() => {
    const fetchTaskTypes = async () => {
      try {
        const response = await api.get('/tasks/classes');
        setTaskTypes(response.data.tasks || []);
      } catch (err: unknown) {
        logger.error('Failed to fetch task types:', { error: err });
      }
    };

    fetchTaskTypes();
  }, [api]);

  // Join task room when taskId changes or socket reconnects
  useEffect(() => {
    if (!taskId || !isConnected) return;

    // Use context's joinTask for ack handling and auto-rejoin on reconnect
    joinTaskRoom(taskId);

    // Refresh status on reconnect to catch events missed during disconnection
    refreshStatus();

    return () => {
      leaveTaskRoom(taskId);
    };
  }, [taskId, isConnected, joinTaskRoom, leaveTaskRoom, refreshStatus]);

  // Socket event handlers
  useEffect(() => {
    if (!socket || !taskId) return;

    const handlers = createTaskSocketHandlers({
      taskId,
      setTaskStatus,
      setError,
      fetchDiff,
    });

    (Object.keys(handlers) as Array<keyof TaskSocketHandlers>).forEach((event) => {
      socket.on(event, handlers[event] as (...args: unknown[]) => void);
    });

    return () => {
      (Object.keys(handlers) as Array<keyof TaskSocketHandlers>).forEach((event) => {
        socket.off(event, handlers[event] as (...args: unknown[]) => void);
      });
    };
  }, [socket, taskId, fetchDiff]);

  // Initial status fetch
  useEffect(() => {
    if (taskId) {
      refreshStatus();
    }
  }, [taskId, refreshStatus]);

  // Recovery: if page is loaded/refreshed while task is at review phase with no diff yet loaded,
  // fetch the diff patch from S3 using the stored diff_key
  useEffect(() => {
    if (taskStatus?.phase === TaskPhase.WAITING_FOR_REVIEW && !taskStatus.preview?.diff) {
      fetchDiff();
    }
  }, [taskStatus?.phase, taskStatus?.preview?.diff, fetchDiff]);

  // Start new task
  const startTask = useCallback(
    async (params: StartTaskParams) => {
      try {
        setLoading(true);
        setError(null);

        const response = await api.post('/tasks/start', params);

        return {
          taskId: response.data.taskId,
          workflowId: response.data.workflowId,
        };
      } catch (err: unknown) {
        const message = axios.isAxiosError(err) ? err.response?.data?.error : undefined;
        setError(message || 'Failed to start task');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [api],
  );

  // Submit answers to questions
  const submitAnswers = useCallback(
    async (answers: Record<string, unknown>) => {
      if (!taskId) return;

      try {
        setLoading(true);
        setError(null);

        await api.post(`/tasks/${taskId}/answers`, { answers });

        // Remember the submitted answers so the form can pre-fill when the
        // user returns to revise (Phase 7 → Phase 3 via `reviseAnswers`).
        setLastAnswers(answers);

        setTaskStatus((prev) =>
          prev
            ? {
                ...prev,
                phase: TaskPhase.PLANNING,
              }
            : null,
        );
      } catch (err: unknown) {
        const message = axios.isAxiosError(err) ? err.response?.data?.error : undefined;
        setError(message || 'Failed to submit answers');
      } finally {
        setLoading(false);
      }
    },
    [taskId, api],
  );

  // Submit review response
  const submitReview = useCallback(
    async (decision: 'approve' | 'reject' | 'request_changes', feedback?: string) => {
      if (!taskId) return;

      try {
        setLoading(true);
        setError(null);

        await api.post(`/tasks/${taskId}/review`, { decision, feedback });

        if (decision === 'approve') {
          setTaskStatus((prev) =>
            prev
              ? {
                  ...prev,
                  phase: TaskPhase.CREATE_PR,
                }
              : null,
          );
        } else if (decision === 'request_changes') {
          setTaskStatus((prev) =>
            prev
              ? {
                  ...prev,
                  phase: TaskPhase.APPLY_CHANGES,
                  iteration: (prev.iteration || 0) + 1,
                }
              : null,
          );
        }
      } catch (err: unknown) {
        const message = axios.isAxiosError(err) ? err.response?.data?.error : undefined;
        setError(message || 'Failed to submit review');
      } finally {
        setLoading(false);
      }
    },
    [taskId, api],
  );

  // Return from review (Phase 7) to the question step (Phase 3) to revise
  // answers. The backend re-emits `task-questions-ready` once the workflow
  // picks up the signal; the existing socket handler drives the phase change.
  const reviseAnswers = useCallback(async () => {
    if (!taskId) return;

    try {
      setLoading(true);
      setError(null);

      await api.post(`/tasks/${taskId}/revise-answers`);

      // Optimistically flip to WAITING_FOR_INPUT so the form re-renders
      // without waiting for the socket round-trip.
      setTaskStatus((prev) =>
        prev
          ? {
              ...prev,
              phase: TaskPhase.WAITING_FOR_INPUT,
            }
          : null,
      );
    } catch (err: unknown) {
      const message = axios.isAxiosError(err) ? err.response?.data?.error : undefined;
      setError(message || 'Failed to revise answers');
    } finally {
      setLoading(false);
    }
  }, [taskId, api]);

  // Cancel task
  const cancelTask = useCallback(async () => {
    if (!taskId) return;

    try {
      setLoading(true);
      setError(null);

      await api.post(`/tasks/${taskId}/cancel`);

      setTaskStatus((prev) =>
        prev
          ? {
              ...prev,
              status: 'cancelled',
            }
          : null,
      );
    } catch (err: unknown) {
      const message = axios.isAxiosError(err) ? err.response?.data?.error : undefined;
      setError(message || 'Failed to cancel task');
    } finally {
      setLoading(false);
    }
  }, [taskId, api]);

  // Retry a failed task
  const retryTask = useCallback(
    async (failedTaskId: string) => {
      try {
        setLoading(true);
        setError(null);

        const response = await api.post(`/tasks/${failedTaskId}/retry`);

        return {
          taskId: response.data.taskId,
          workflowId: response.data.workflowId,
        };
      } catch (err: unknown) {
        const message = axios.isAxiosError(err) ? err.response?.data?.error : undefined;
        setError(message || 'Failed to retry task');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [api],
  );

  return {
    taskStatus,
    loading,
    error,
    taskTypes,
    startTask,
    submitAnswers,
    submitReview,
    reviseAnswers,
    cancelTask,
    retryTask,
    // Prefer the fresh in-session state; fall back to the persisted value
    // from getTaskStatus so the form pre-fills correctly after a page
    // refresh during a revise-plan round-trip.
    lastAnswers: lastAnswers ?? taskStatus?.userAnswers ?? undefined,
  };
}
